import { useAPI } from "../hooks/useAPI";
import RepositoryCache from "./RepositoryCache";

const defaultTTL = 5 * 60 * 1000;
const sharedCache = new RepositoryCache(100, defaultTTL);

export class BaseRepository {
  constructor() {
    this.api = null;
    this.cache = sharedCache;
    this.namespace = this.constructor.name;
  }

  setAPI(api) {
    this.api = api;
  }

  getCached(key) {
    key = this.cache.createKey(this.namespace, key);
    return this.cache.get(key);
  }

  setCached(key, data, ttl = defaultTTL) {
    key = this.cache.createKey(this.namespace, key);
    this.cache.set(key, data, ttl);
  }

  async get(endpoint) {
    return this.api.get(endpoint);
  }

  async post(endpoint, data) {
    return this.api.post(endpoint, data);
  }

  async put(endpoint, data) {
    return this.api.put(endpoint, data);
  }

  async executeQuery(query) {
    const { data, error } = await query;
    if (error) throw error;
    return data;
  }
}

// Singleton instances for each repository class
const repositoryInstances = new Map();

/**
 * Hook to get a repository instance. Returns the correct repository type based on the class passed.
 * @template {typeof BaseRepository} T
 * @param {T} RepositoryClass - The repository class to instantiate
 * @returns {InstanceType<T>} An instance of the repository class
 */
export const useRepository = (RepositoryClass) => {
  const api = useAPI();

  // Get or create singleton instance
  if (!repositoryInstances.has(RepositoryClass)) {
    const repository = new RepositoryClass();
    repositoryInstances.set(RepositoryClass, repository);
  }

  const repository = repositoryInstances.get(RepositoryClass);
  repository.setAPI(api);
  return repository;
};
